import {
  Box,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  Stack,
} from '@mui/material';
import {
  FieldWrapperVertical,
  ReadOnly,
  useEntityPermission,
  useFormPermissions,
} from '@ui-framework/index';
import { SelectStatic } from '@product-framework/components/FormFields';
import { forwardRef, useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import {
  MONTHS,
  WEEK_OF_MONTH_OPTIONS,
  WEEKDAYS_LONG,
} from '../../../../utils/constants';

const RecurrenceOptionsPatternYearly = forwardRef((props, ref) => {
  const {
    control,
    setValue,
    formState: { errors },
    getValues,
  } = useFormContext();
  const { permissions } = useFormPermissions();
  const { isReadOnly } = useEntityPermission();

  const currentValues = getValues();

  const recurrenceYearlyMode = useWatch({
    control,
    name: 'recurrenceYearlyMode',
  });

  const recurrenceYearlyMode1MM = useWatch({
    control,
    name: 'recurrenceYearlyMode1MM',
  });

  const getDaysForMonth = month => {
    const daysInMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

    return Array.from({ length: daysInMonth[month - 1] }, (_, i) => ({
      label: i + 1,
      value: i + 1,
    }));
  };

  useEffect(() => {
    if (!recurrenceYearlyMode) {
      setValue('recurrenceYearlyMode', 'MONTH_DAY');
    }
  }, [recurrenceYearlyMode]);

  useEffect(() => {
    if (!recurrenceYearlyMode1MM) {
      setValue('recurrenceYearlyMode1MD', '');
    }
  }, [recurrenceYearlyMode1MM]);

  if (isReadOnly(permissions?.recurrenceYearlyMode, recurrenceYearlyMode)) {
    if (recurrenceYearlyMode === 'MONTH_DAY') {
      return (
        <ReadOnly name="recurrenceYearlyMode">
          <FormattedMessage
            id="EVERY_MONTH_DAY"
            defaultMessage="Every {month} {day}"
            values={{
              month: MONTHS.find(
                m => m.value === currentValues?.recurrenceYearlyMode1MM,
              ).label,
              day: currentValues?.recurrenceYearlyMode1MD,
            }}
          />
        </ReadOnly>
      );
    }
    if (recurrenceYearlyMode === 'ORDINAL_WEEKDAY_IN_MONTH') {
      return (
        <ReadOnly name="recurrenceYearlyMode">
          <FormattedMessage
            id="THE_ORDINAL_WEEKDAY_OF_MONTH"
            defaultMessage="The {ordinal} {weekday} of {month}"
            values={{
              ordinal: WEEK_OF_MONTH_OPTIONS.find(
                m => m.value === currentValues?.recurrenceYearlyMode2OWMO,
              ).label,
              weekday: WEEKDAYS_LONG.find(
                m => m.value === currentValues?.recurrenceYearlyMode2OWMW,
              ).label,
              month: MONTHS.find(
                m => m.value === currentValues?.recurrenceYearlyMode2OWMM,
              ).label,
            }}
          />
        </ReadOnly>
      );
    }
  }

  return (
    <Stack spacing={2} ref={ref}>
      <FieldWrapperVertical
        field={{
          name: 'recurrenceYearlyMode',
          label: {
            localeId: 'YEARLY_OPTION',
            defaultMessage: 'Yearly Options',
          },
          required: true,
        }}
        errors={errors}>
        <Controller
          control={control}
          name="recurrenceYearlyMode"
          render={({ field, fieldState: { error } }) => {
            return (
              <Box>
                <RadioGroup {...field}>
                  <Stack spacing={2}>
                    <Stack
                      sx={{ display: 'flex', alignItems: 'center' }}
                      direction="row"
                      spacing={1}>
                      <FormControlLabel
                        value="MONTH_DAY"
                        control={<Radio size="small" />}
                        label="Every"
                      />
                      <SelectStatic
                        name="recurrenceYearlyMode1MM"
                        disabled={recurrenceYearlyMode !== 'MONTH_DAY'}
                        customConfig={{ options: MONTHS }}
                        permission={permissions?.recurrenceYearlyMode1MM}
                        field={{ name: 'recurrenceYearlyMode1MM' }}
                      />
                      <SelectStatic
                        name="recurrenceYearlyMode1MD"
                        disabled={recurrenceYearlyMode !== 'MONTH_DAY'}
                        customConfig={{
                          options: getDaysForMonth(recurrenceYearlyMode1MM),
                        }}
                        permission={permissions?.recurrenceYearlyMode1MD}
                        field={{ name: 'recurrenceYearlyMode1MD' }}
                      />
                    </Stack>
                    <Stack
                      sx={{ display: 'flex', alignItems: 'center' }}
                      direction="row"
                      spacing={1}>
                      <FormControlLabel
                        value="ORDINAL_WEEKDAY_IN_MONTH"
                        control={<Radio size="small" />}
                        label="The"
                      />
                      <SelectStatic
                        name="recurrenceYearlyMode2OWMO"
                        disabled={
                          recurrenceYearlyMode !== 'ORDINAL_WEEKDAY_IN_MONTH'
                        }
                        customConfig={{ options: WEEK_OF_MONTH_OPTIONS }}
                        permission={permissions?.recurrenceYearlyMode2OWMO}
                        field={{ name: 'recurrenceYearlyMode2OWMO' }}
                      />
                      <SelectStatic
                        name="recurrenceYearlyMode2OWMW"
                        disabled={
                          recurrenceYearlyMode !== 'ORDINAL_WEEKDAY_IN_MONTH'
                        }
                        customConfig={{ options: WEEKDAYS_LONG }}
                        permission={permissions?.recurrenceYearlyMode2OWMW}
                        field={{ name: 'recurrenceYearlyMode2OWMW' }}
                      />
                      <Box>
                        <FormattedMessage id="OF" defaultMessage="of" />
                      </Box>
                      <SelectStatic
                        name="recurrenceYearlyMode2OWMM"
                        disabled={
                          recurrenceYearlyMode !== 'ORDINAL_WEEKDAY_IN_MONTH'
                        }
                        customConfig={{ options: MONTHS }}
                        permission={permissions?.recurrenceYearlyMode2OWMM}
                        field={{ name: 'recurrenceYearlyMode2OWMM' }}
                      />
                    </Stack>
                  </Stack>
                </RadioGroup>
                {error?.message && (
                  <Box sx={{ ml: 2, mt: 1 }}>
                    <FormHelperText error={Boolean(error?.message)}>
                      {error?.message}
                    </FormHelperText>
                  </Box>
                )}
              </Box>
            );
          }}
        />
      </FieldWrapperVertical>
    </Stack>
  );
});

export default RecurrenceOptionsPatternYearly;
